import React, {
	useEffect,
	useReducer,
	useState,
	createContext,
	useCallback,
	useRef,
} from 'react'
import { TranslationFile } from 'i18n'
import { ProgressBar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import './Multistep.scss'

export interface IMultistepComponent {
	id: number
	header?: string
	body: any
	stepCompleted: boolean
	show?: boolean
	nbrOfSubsteps?: number
}

interface IMultistepProps {
	steps: number[]
	index: number
	progressBarIndexArray: number[]
	progressBarIndex: number
	substepIndex: number
	nextButtonText: string
	hidePreviousBtn: boolean
}

interface IProps {
	multisteps: IMultistepComponent[]
	hidePreviousButton?: boolean
	resetInitialState: boolean
}

const initialState: IMultistepProps = {
	steps: [],
	index: 0,
	progressBarIndexArray: [],
	progressBarIndex: 0,
	substepIndex: 0,
	nextButtonText: 'Next',
	hidePreviousBtn: true,
}

export const MSP_ACTIONS = {
	SET_STEPS: 'SET_STEPS',
	SET_INDEX: 'SET_INDEX',
	SET_STEPS_AND_INDEX: 'SET_STEPS_AND_INDEX',
	SET_NEXTBUTTON_TEXT: 'SET_NEXTBUTTON_TEXT',
	SET_NEXTBUTTON_FUNCTION: 'SET_NEXTBUTTON_FUNCTION',
	SET_HIDE_PREVIOUS_BUTTON: 'SET_HIDE_PREVIOUS_BUTTON',
	RESET_STEPS: 'RESET_STEPS',
	SET_SUBSTEP_INDEX: 'SET_SUBSTEP_INDEX',
	SET_PROGRESS_BAR_INDEX_ARRAY: 'SET_PROGRESS_BAR_INDEX_ARRAY',
	SET_PROGRESS_BAR_INDEX: 'SET_PROGRESS_BAR_INDEX',
}

var initialMultisteps: IMultistepComponent[] = []

function reducer(msp: any, action: any) {
	switch (action.type) {
		case MSP_ACTIONS.SET_STEPS:
			return {
				...msp,
				steps: action.payload.steps,
			}
		case MSP_ACTIONS.SET_INDEX:
			return {
				...msp,
				index: action.payload.index,
			}
		case MSP_ACTIONS.SET_STEPS_AND_INDEX:
			return {
				...msp,
				steps: action.payload.steps,
				index: action.payload.index,
			}
		case MSP_ACTIONS.SET_NEXTBUTTON_TEXT:
			return {
				...msp,
				nextButtonText: action.payload.nextButtonText,
			}
		case MSP_ACTIONS.SET_HIDE_PREVIOUS_BUTTON:
			return {
				...msp,
				hidePreviousBtn: action.payload.hidePreviousBtn,
			}
		case MSP_ACTIONS.RESET_STEPS:
			const activeSteps = initialMultisteps.filter(
				(step) => step.show === true || step.show === undefined
			)
			const activeStepsIds = activeSteps.map((x) => x.id)

			const nbrOfProgressBarSteps = activeSteps
				.map((step) => step.nbrOfSubsteps || 1)
				.reduce((sum, substeps) => sum + substeps, 0)
			const progressBarIndexArray = Array.from(
				{ length: nbrOfProgressBarSteps },
				(_, i) => i
			)
			return {
				...msp,
				steps: activeStepsIds,
				index: 0,
				substepIndex: 0,
				progressBarIndexArray: progressBarIndexArray,
				progressBarIndex: 0,
			}
		case MSP_ACTIONS.SET_SUBSTEP_INDEX:
			const currentStep = initialMultisteps.find(
				(step) => step.id === msp.steps[msp.index]
			)
			return {
				...msp,
				substepIndex:
					currentStep &&
					0 <= action.payload.substepIndex &&
					action.payload.substepIndex <
						(currentStep.nbrOfSubsteps || 1)
						? action.payload.substepIndex
						: msp.substepIndex, // keep old value if index out of bounds
			}
		case MSP_ACTIONS.SET_PROGRESS_BAR_INDEX_ARRAY:
			return {
				...msp,
				progressBarIndexArray: action.payload.progressBarIndexArray,
			}
		case MSP_ACTIONS.SET_PROGRESS_BAR_INDEX:
			return {
				...msp,
				progressBarIndex:
					0 <= action.payload.progressBarIndex &&
					action.payload.progressBarIndex <
						msp.progressBarIndexArray.length
						? action.payload.progressBarIndex
						: msp.progressBarIndex, // keep old value if index out of bounds
			}
		default:
			return msp
	}
}

export const MspDispatchContext = createContext<{
	msp: IMultistepProps
	dispatchMsp: any
}>({ msp: initialState, dispatchMsp: reducer })

function Multistep({
	multisteps,
	hidePreviousButton = true,
	resetInitialState,
}: IProps) {
	const { t } = useTranslation([TranslationFile.Translation])

	const [msp, dispatchMsp] = useReducer(reducer, initialState)

	const [isFirstPage, setIsFirstPage] = useState<boolean>(true)
	const [isLastPage, setIsLastPage] = useState<boolean>(false)
	const [currentStep, setCurrentStep] = useState<IMultistepComponent>()
	const currentStepBodyRef = useRef<HTMLDivElement>(null)
	const [reset, setReset] = useState<boolean>(resetInitialState)
	const [hidePreviousBtn] = useState<boolean>(hidePreviousButton)
	const [currentStepCompletionRate, setCurrentStepCompletionRate] =
		useState<number>(0)

	const mspStorageName = 'msp'

	initialMultisteps = multisteps

	const getMultisteps = useCallback(() => {
		const savedSteps = sessionStorage.getItem(mspStorageName)

		if (!savedSteps) {
			dispatchMsp({
				type: MSP_ACTIONS.RESET_STEPS,
			})
		} else if (reset) {
			dispatchMsp({
				type: MSP_ACTIONS.RESET_STEPS,
			})
			setReset(false)
		} else {
			let item = JSON.parse(savedSteps)
			dispatchMsp({
				type: MSP_ACTIONS.SET_STEPS_AND_INDEX,
				payload: {
					steps: item.steps,
					index: item.index,
				},
			})
			dispatchMsp({
				type: MSP_ACTIONS.SET_SUBSTEP_INDEX,
				payload: {
					substepIndex: item.substepIndex,
				},
			})
			dispatchMsp({
				type: MSP_ACTIONS.SET_PROGRESS_BAR_INDEX_ARRAY,
				payload: {
					progressBarIndexArray: item.progressBarIndexArray,
				},
			})
			dispatchMsp({
				type: MSP_ACTIONS.SET_PROGRESS_BAR_INDEX,
				payload: {
					progressBarIndex: item.progressBarIndex,
				},
			})
		}
	}, [reset])

	useEffect(() => {
		getMultisteps()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (msp) {
			setCurrentStep(
				multisteps.find((x) => x.id === msp.steps[msp.index])
			)
			setIsFirstPage(msp.index === 0)
			setIsLastPage(msp.index === msp?.steps.length - 1)
			sessionStorage.setItem(mspStorageName, JSON.stringify(msp))
		}
	}, [msp, multisteps])

	useEffect(() => {
		const mscontainer = document.getElementById('multistep')
		const mscontainerrect = mscontainer?.getBoundingClientRect()
		if (msp.index > 0 && mscontainerrect !== undefined) {
			window.scrollTo({
				left: mscontainerrect.left + window.scrollX,
				top: mscontainerrect.top + window.scrollY - 100,
				behavior: 'smooth',
			})
		} else {
			scrollToTop()
		}
	}, [msp.index])

	useEffect(() => {
		const progressBarIndex =
			multisteps
				.filter((step) => msp.steps.includes(step.id))
				.filter((_, index) => index < msp.index)
				.reduce((sum, step) => sum + (step.nbrOfSubsteps || 1), 0) +
			msp.substepIndex
		dispatchMsp({
			type: MSP_ACTIONS.SET_PROGRESS_BAR_INDEX,
			payload: {
				progressBarIndex: progressBarIndex,
			},
		})
	}, [msp.index, msp.steps, msp.substepIndex, multisteps])

	const calculateCompletionRate = useCallback(() => {
		if (!currentStepBodyRef.current) return

		const requiredElementsInCurrentBody =
			currentStepBodyRef.current.querySelectorAll(
				'.required-step, #main-form div.required'
			)
		const completedElementsInCurrentBody =
			currentStepBodyRef.current.querySelectorAll(
				'.required-step.completed-step, #main-form div.required:not(.has-error):not(.is-empty)'
			)
		setCurrentStepCompletionRate(
			requiredElementsInCurrentBody.length === 0
				? 1
				: completedElementsInCurrentBody.length /
						requiredElementsInCurrentBody.length
		)
	}, [currentStepBodyRef])

	const dynamicallyAddedNodes = useRef(new Set<Node>())
	const formBuilderNodes = useRef(new Set<Node>())

	useEffect(() => {
		if (!currentStepBodyRef.current) return

		calculateCompletionRate()

		const relevantClasses = [
			'required-step',
			'completed-step',
			'required',
			'completed',
			'col-spinner',
		]

		const observer = new MutationObserver((mutations) => {
			let relevantMutations = false
			mutations.forEach((mutation) => {
				if (!currentStepBodyRef.current) return
				if (
					mutation.type === 'attributes' &&
					mutation.attributeName === 'class'
				) {
					relevantMutations = true
				} else if (
					mutation.type === 'childList' &&
					(mutation.addedNodes.length > 0 ||
						mutation.removedNodes.length > 0)
				) {
					mutation.addedNodes.forEach((node) => {
						if (node instanceof HTMLElement) {
							if (
								node.querySelector(
									relevantClasses
										.map((cls) => `.${cls}`)
										.join(', ')
								)
							) {
								relevantMutations = true
								return // exit mutation.addedNodes.forEach
							} else if (
								node.closest('.has-dynamically-added-children')
							) {
								dynamicallyAddedNodes.current.add(node)
								relevantMutations = true
								return // exit mutation.addedNodes.forEach
							} else if (node.closest('#main-form')) {
								formBuilderNodes.current.add(node)
								relevantMutations = true
								return // exit mutation.addedNodes.forEach
							}
						}
					})
					mutation.removedNodes.forEach((node) => {
						if (node instanceof HTMLElement) {
							if (
								node.querySelector(
									relevantClasses
										.map((cls) => `.${cls}`)
										.join(', ')
								)
							) {
								relevantMutations = true
								return // exit mutation.removedNodes.forEach
							} else if (
								dynamicallyAddedNodes.current.has(node)
							) {
								dynamicallyAddedNodes.current.delete(node)
								relevantMutations = true
								return // exit mutation.removedNodes.forEach
							} else if (formBuilderNodes.current.has(node)) {
								formBuilderNodes.current.delete(node)
								relevantMutations = true
								return // exit mutation.removedNodes.forEach
							}
						}
					})
				}
				if (relevantMutations) return // exit mutations.forEach
			})
			if (relevantMutations) calculateCompletionRate()
		})

		observer.observe(currentStepBodyRef.current, {
			attributes: true, // observe attribute changes
			attributeFilter: ['class'], // only watch for class attribute changes
			subtree: true, // observe the entire subtree (and not just the outermost element level)
			childList: true, // detect added or removed child nodes
		})

		return () => observer.disconnect()
	}, [calculateCompletionRate, dynamicallyAddedNodes])

	const nextPage = () => {
		if (!isLastPage && msp) {
			dispatchMsp({
				type: MSP_ACTIONS.SET_INDEX,
				payload: {
					index: msp.index + 1,
				},
			})
			dispatchMsp({
				type: MSP_ACTIONS.SET_PROGRESS_BAR_INDEX,
				payload: {
					progressBarIndex: msp.progressBarIndex + 1,
				},
			})
			dispatchMsp({
				type: MSP_ACTIONS.SET_SUBSTEP_INDEX,
				payload: {
					substepIndex: 0,
				},
			})
		}
	}

	const previousPage = () => {
		if (!isFirstPage && msp) {
			dispatchMsp({
				type: MSP_ACTIONS.SET_INDEX,
				payload: {
					index: msp.index - 1,
				},
			})
			dispatchMsp({
				type: MSP_ACTIONS.SET_PROGRESS_BAR_INDEX,
				payload: {
					progressBarIndex: msp.progressBarIndex - 1,
				},
			})
			dispatchMsp({
				type: MSP_ACTIONS.SET_SUBSTEP_INDEX,
				payload: {
					substepIndex: 0,
				},
			})
		}
	}

	const scrollToTop = () => {
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth',
		})
	}

	const progressBarRefs = useRef<(HTMLDivElement | null)[]>([])
	useEffect(() => {
		progressBarRefs.current.forEach((ref, index) => {
			if (ref) {
				var completenessRate =
					index < msp.progressBarIndex
						? 1
						: index === msp.progressBarIndex
						? currentStepCompletionRate
						: 0
				const bar = ref.querySelector('[role="progressbar"]')
				if (bar instanceof HTMLElement) {
					bar.setAttribute(
						'aria-label',
						t('WCAG-Label_ProgressBar', {
							step: index + 1,
							percentage: Math.round(100 * completenessRate),
						})
					)
				}
			}
		})
	})

	const getProgressBar = useCallback(() => {
		return (
			<div className={`progress-container rounded my-3`}>
				{msp.progressBarIndexArray.map((index: number) => {
					return (
						<ProgressBar
							key={index}
							ref={(elem) =>
								(progressBarRefs.current[index] = elem)
							}
							variant="info"
							min={0}
							max={1}
							now={
								index < msp.progressBarIndex
									? 1
									: index === msp.progressBarIndex
									? currentStepCompletionRate
									: 0
							}
						/>
					)
				})}
			</div>
		)
	}, [
		currentStepCompletionRate,
		msp.progressBarIndexArray,
		msp.progressBarIndex,
	])

	const getPagination = () => {
		return (
			<div className="d-flex justify-content-center pagination-container">
				{!isFirstPage && !hidePreviousBtn && !isLastPage && (
					<button
						className="btn btn-outline-darkgray mx-2"
						onClick={() => {
							previousPage()
						}}>
						{t('Previous')}
					</button>
				)}
				{!isLastPage && currentStep?.stepCompleted && (
					<button
						className="btn btn-primary mx-2"
						onClick={() => {
							setTimeout(() => {
								nextPage()
							}, 400)
						}}>
						{t(`${msp.nextButtonText}`)}
					</button>
				)}
			</div>
		)
	}

	const getBody = () => {
		return (
			<div>
				<div className="d-flex flex-column multistep-container">
					<div className="multistep-content">
						<h2 className="multistep-header">
							{t(`${currentStep?.header}`)}
						</h2>
					</div>
					<hr />
					<div ref={currentStepBodyRef} className="multistep-content">
						{currentStep?.body}
					</div>
				</div>
				{getPagination()}
			</div>
		)
	}

	return (
		<MspDispatchContext.Provider value={{ msp, dispatchMsp }}>
			<div id="multistep">
				{getProgressBar()}
				{getBody()}
			</div>
		</MspDispatchContext.Provider>
	)
}
export default Multistep